/*
  Sheme k vprašanjem zaključnega testa.

  Namen ni lepota, ampak da tečajnik takoj vidi, za kakšno situacijo gre.
  Zato povsod ista govorica:
    · siva črta            = tla ali pobočje
    · oranžna              = krilo in pilot
    · zelena s kljukico    = pravilno
    · rdeča črtkana s ✗    = napačno
    · tanke puščice        = veter ali gibanje zraka

  Risane so kot inline SVG in ne kot slike, zato:
  - se barve ujemajo s temo (uporabljajo iste tokene kot app),
  - ostanejo ostre na vsakem zaslonu,
  - ne potrebujejo nalaganja v Storage.

  Če Luka k vprašanju naloži svojo fotografijo (`slika_url`), ta prevlada
  nad shemo — glej FinalTest.

  Vprašanja 3, 9, 13 in 18 sheme nimajo: gre za zaporedje dejanj ali
  odločitev, ne za prostorsko situacijo, in risba bi dodala samo šum.
*/

const W = 320;
const H = 180;

const C = {
  ink: 'var(--text)',
  dim: 'var(--text-3)',
  brand: 'var(--brand)',
  ok: 'var(--ok-fill)',
  bad: 'var(--bad-fill)',
  line: 'var(--border-strong)',
};

/* ── Gradniki ───────────────────────────────────────────── */

const Ground = ({ y = 158 }) => (
  <line x1="8" y1={y} x2={W - 8} y2={y} stroke={C.line} strokeWidth="3" strokeLinecap="round" />
);

/** Pobočje. `side` = 'left' | 'right' */
const Hill = ({ side = 'left' }) =>
  side === 'left' ? (
    <path d="M8 158 L8 60 L120 158 Z" fill="var(--surface-3)" stroke={C.line} strokeWidth="2" />
  ) : (
    <path d={`M${W - 8} 158 L${W - 8} 60 L${W - 120} 158 Z`} fill="var(--surface-3)" stroke={C.line} strokeWidth="2" />
  );

/** Jadralno padalo: lok krila, vrvice, pilot. */
const Glider = ({ x, y, scale = 1, flip = false, color = C.brand }) => (
  <g transform={`translate(${x} ${y}) scale(${flip ? -scale : scale} ${scale})`}>
    <path d="M-16 0 Q0 -11 16 0" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" />
    <line x1="-12" y1="1.5" x2="0" y2="15" stroke={color} strokeWidth="1.4" />
    <line x1="12" y1="1.5" x2="0" y2="15" stroke={color} strokeWidth="1.4" />
    <circle cx="0" cy="17" r="3.6" fill={color} />
  </g>
);

const Arrow = ({ d, color = C.dim, dash, width = 2 }) => (
  <path
    d={d}
    fill="none"
    stroke={color}
    strokeWidth={width}
    strokeDasharray={dash}
    strokeLinecap="round"
    markerEnd="url(#pt)"
  />
);

const Label = ({ x, y, children, color = C.ink, anchor = 'middle', size = 11, bold }) => (
  <text
    x={x}
    y={y}
    fill={color}
    fontSize={size}
    fontWeight={bold ? 700 : 500}
    textAnchor={anchor}
    fontFamily="var(--font)"
  >
    {children}
  </text>
);

const Tick = ({ x, y }) => (
  <g transform={`translate(${x} ${y})`}>
    <circle r="8" fill={C.ok} />
    <path d="M-3.5 0 L-1 2.5 L3.5 -2.5" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </g>
);

const Cross = ({ x, y }) => (
  <g transform={`translate(${x} ${y})`}>
    <circle r="8" fill={C.bad} />
    <path d="M-3 -3 L3 3 M3 -3 L-3 3" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
  </g>
);

/* ── Sheme po vprašanjih ────────────────────────────────── */

const D = {
  // 1 — pet kontrolnih točk
  1: (
    <>
      <Glider x={70} y={64} scale={1.6} />
      <Label x={70} y={128} color={C.dim} size={10}>
        pilot pripravljen
      </Label>
      {[
        'krilo',
        'vrvice',
        'vpetost v sedež',
        'smer vetra',
        'prost zračni prostor',
      ].map((t, i) => (
        <g key={t}>
          <circle cx={164} cy={38 + i * 25} r="9" fill={C.brand} />
          <text x={164} y={42 + i * 25} fill="#fff" fontSize="11" fontWeight="700" textAnchor="middle">
            {i + 1}
          </text>
          <Label x={181} y={42 + i * 25} anchor="start">
            {t}
          </Label>
        </g>
      ))}
    </>
  ),

  // 2 — v zavoju je propadanje večje
  2: (
    <>
      <Ground />
      <Glider x={44} y={34} scale={0.9} />
      <Arrow d="M58 46 L286 132" color={C.ok} />
      <Label x={250} y={126} color={C.ok} anchor="end" size={10} bold>
        naravnost
      </Label>
      <Arrow d="M58 50 Q120 96 116 150" color={C.bad} />
      <Label x={128} y={148} color={C.bad} anchor="start" size={10} bold>
        v zavoju — hitreje dol
      </Label>
    </>
  ),

  // 4 — prekinitev vzleta
  4: (
    <>
      <Ground />
      <g>
        <Label x={80} y={22} bold>
          enostranski poteg
        </Label>
        <path d="M42 58 Q64 40 96 62" fill="none" stroke={C.brand} strokeWidth="3" strokeLinecap="round" />
        <line x1="58" y1="60" x2="70" y2="126" stroke={C.brand} strokeWidth="1.4" />
        <line x1="92" y1="62" x2="70" y2="126" stroke={C.brand} strokeWidth="1.4" />
        <circle cx="70" cy="130" r="4" fill={C.brand} />
        <Arrow d="M96 66 L116 96" color={C.ok} width={2.4} />
        <Label x={80} y={152} color={C.ok} size={10} bold>
          krilo pade na stran
        </Label>
        <Tick x={122} y={26} />
      </g>
      <line x1="160" y1="14" x2="160" y2="166" stroke={C.line} strokeWidth="1" strokeDasharray="3 4" />
      <g>
        <Label x={244} y={22} bold>
          obe zavori
        </Label>
        <path d="M208 52 Q244 34 280 52" fill="none" stroke={C.brand} strokeWidth="3" strokeLinecap="round" />
        <line x1="216" y1="54" x2="244" y2="120" stroke={C.brand} strokeWidth="1.4" />
        <line x1="272" y1="54" x2="244" y2="120" stroke={C.brand} strokeWidth="1.4" />
        <circle cx="244" cy="124" r="4" fill={C.brand} />
        <Arrow d="M244 116 L244 84" color={C.bad} width={2.4} />
        <Label x={244} y={152} color={C.bad} size={10} bold>
          vzgon te dvigne
        </Label>
        <Cross x={292} y={26} />
      </g>
    </>
  ),

  // 5 — ob pobočju se ne obračaj proti hribu
  5: (
    <>
      <Hill side="left" />
      <Ground />
      <Glider x={186} y={70} scale={1} />
      <Arrow d="M170 80 Q132 92 108 110" color={C.bad} dash="5 4" width={2.4} />
      <Cross x={100} y={116} />
      <Label x={126} y={136} color={C.bad} size={10} bold>
        proti pobočju
      </Label>
      <Arrow d="M202 80 Q244 92 276 84" color={C.ok} width={2.4} />
      <Tick x={286} y={80} />
      <Label x={246} y={62} color={C.ok} size={10} bold>
        stran od pobočja
      </Label>
    </>
  ),

  // 6 — enostransko zapiranje
  6: (
    <>
      <path d="M120 52 Q168 26 214 50" fill="none" stroke={C.brand} strokeWidth="3.4" strokeLinecap="round" />
      <path d="M92 62 Q106 44 120 52" fill="none" stroke={C.bad} strokeWidth="3.4" strokeLinecap="round" strokeDasharray="4 3" />
      <Label x={96} y={38} color={C.bad} size={10} bold anchor="start">
        zaprta stran
      </Label>
      <line x1="128" y1="54" x2="166" y2="122" stroke={C.brand} strokeWidth="1.4" />
      <line x1="208" y1="54" x2="166" y2="122" stroke={C.brand} strokeWidth="1.4" />
      <circle cx="166" cy="126" r="4.5" fill={C.brand} />
      <Arrow d="M182 128 L214 128" color={C.ok} width={2.4} />
      <Label x={218} y={132} color={C.ok} anchor="start" size={10} bold>
        1. teža na odprto
      </Label>
      <Arrow d="M150 120 L112 84" color={C.ok} width={2.4} />
      <Label x={108} y={78} color={C.ok} anchor="end" size={10} bold>
        2. pumpaj
      </Label>
      <Label x={160} y={166} color={C.dim} size={10}>
        najprej ustavi vrtenje, šele nato pumpaj
      </Label>
    </>
  ),

  // 7 — zavetrna stran in rotor
  7: (
    <>
      {/* Tu hrib potrebuje pravi vrh: zrak mora iti ČEZ njega, da se za
          njim naredi rotor. Splošni <Hill> je klanec in bi zavajal. */}
      <path
        d="M8 158 L92 48 L164 158 Z"
        fill="var(--surface-3)"
        stroke={C.line}
        strokeWidth="2"
      />
      <Ground />
      <Arrow d="M12 34 L74 34" />
      <Arrow d="M12 18 L88 18" />
      <Label x={14} y={52} color={C.dim} anchor="start" size={10}>
        veter
      </Label>
      <Arrow d="M96 30 Q130 26 156 44" width={2} />

      {/* Spirali navznoter se bereta kot vrtenje. */}
      <path
        d="M206 76 a26 26 0 1 1 -25 26 a17 17 0 1 0 17 -17"
        fill="none"
        stroke={C.bad}
        strokeWidth="2.4"
        strokeLinecap="round"
        markerEnd="url(#pt)"
      />
      <path
        d="M272 96 a19 19 0 1 1 -18 19 a12 12 0 1 0 12 -12"
        fill="none"
        stroke={C.bad}
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.75"
        markerEnd="url(#pt)"
      />

      <Cross x={294} y={44} />
      <Label x={280} y={48} color={C.bad} anchor="end" size={10} bold>
        rotor
      </Label>
      <Label x={222} y={172} color={C.dim} size={10}>
        v zavetrje se ne spuščaj
      </Label>
    </>
  ),

  // 8 — zmanjka višine pred pristankom
  8: (
    <>
      <Ground />
      <Glider x={52} y={44} scale={0.9} />
      <rect x="196" y="140" width="104" height="18" rx="3" fill="var(--surface-3)" stroke={C.line} strokeWidth="1.5" />
      <Label x={248} y={153} color={C.dim} size={10}>
        pristajališče
      </Label>
      <Arrow d="M66 58 Q140 96 196 142" color={C.bad} dash="5 4" width={2.4} />
      <Cross x={150} y={92} />
      <Label x={158} y={86} color={C.bad} anchor="start" size={10} bold>
        prisilni zavoj nazaj
      </Label>
      <rect x="76" y="126" width="80" height="16" rx="3" fill="none" stroke={C.ok} strokeWidth="2" strokeDasharray="4 3" />
      <Arrow d="M62 60 L110 122" color={C.ok} width={2.4} />
      <Tick x={116} y={112} />
      <Label x={116} y={138} color={C.ok} size={10} bold>
        najbližje varno
      </Label>
    </>
  ),

  // 10 — predolgo močno zaviranje
  10: (
    <>
      <g>
        <Label x={80} y={24} bold>
          odmerjeno
        </Label>
        <path d="M36 66 Q80 44 124 66" fill="none" stroke={C.brand} strokeWidth="3.2" strokeLinecap="round" />
        {[0, 1, 2, 3].map((i) => (
          <Arrow key={i} d={`M28 ${84 + i * 12} L118 ${84 + i * 12}`} color={C.ok} width={1.6} />
        ))}
        <Label x={80} y={148} color={C.ok} size={10} bold>
          tok se drži krila
        </Label>
        <Tick x={126} y={28} />
      </g>
      <line x1="160" y1="14" x2="160" y2="166" stroke={C.line} strokeWidth="1" strokeDasharray="3 4" />
      <g>
        <Label x={244} y={24} bold>
          premočno
        </Label>
        <path d="M200 66 Q244 44 288 66" fill="none" stroke={C.brand} strokeWidth="3.2" strokeLinecap="round" />
        {[0, 1].map((i) => (
          <Arrow key={i} d={`M192 ${84 + i * 12} L232 ${84 + i * 12}`} color={C.dim} width={1.6} />
        ))}
        <path d="M236 84 Q252 96 244 108 Q236 120 254 128" fill="none" stroke={C.bad} strokeWidth="2" strokeLinecap="round" />
        <path d="M240 96 Q258 106 250 120" fill="none" stroke={C.bad} strokeWidth="2" strokeLinecap="round" opacity="0.7" />
        <Label x={244} y={148} color={C.bad} size={10} bold>
          tok se odtrga — stall
        </Label>
        <Cross x={294} y={28} />
      </g>
    </>
  ),

  // 11 — vzletni prostor
  11: (
    <>
      <Hill side="left" />
      <Ground />
      <line x1="18" y1="120" x2="112" y2="120" stroke={C.ok} strokeWidth="3" strokeLinecap="round" />
      <line x1="18" y1="112" x2="18" y2="128" stroke={C.ok} strokeWidth="2.5" />
      <line x1="112" y1="112" x2="112" y2="128" stroke={C.ok} strokeWidth="2.5" />
      <Label x={66} y={108} color={C.ok} size={10} bold>
        dovolj dolgo
      </Label>
      <Label x={66} y={142} color={C.dim} size={10}>
        prostor za prekinitev
      </Label>
      <Glider x={188} y={54} scale={0.95} />
      <Tick x={276} y={40} />
      <Label x={264} y={44} color={C.ok} anchor="end" size={10} bold>
        vzlet lahko prekineš
      </Label>
    </>
  ),

  // 12 — hrbtni veter v finalnem doletu
  12: (
    <>
      <Ground />
      <Arrow d="M14 34 L84 34" />
      <Label x={16} y={26} color={C.dim} anchor="start" size={10}>
        veter v hrbet
      </Label>
      <Glider x={104} y={58} scale={0.9} />
      <Arrow d="M120 70 L262 132" color={C.ok} width={2.4} />
      <Tick x={276} y={140} />
      <Label x={196} y={92} color={C.ok} anchor="middle" size={10} bold>
        leti naprej
      </Label>
      <Label x={196} y={106} color={C.dim} anchor="middle" size={10}>
        hitrejši pristanek
      </Label>
      <Arrow d="M104 76 Q52 104 104 126" color={C.bad} dash="5 4" width={2.4} />
      <Cross x={112} y={132} />
      <Label x={72} y={150} color={C.bad} size={10} bold>
        nizek zavoj
      </Label>
    </>
  ),

  // 14 — kdo se umakne ob pobočju
  14: (
    <>
      <Hill side="left" />
      <Ground />
      <Glider x={152} y={72} scale={0.85} />
      <Label x={152} y={44} color={C.dim} size={10}>
        pobočje na levi
      </Label>
      <Arrow d="M168 84 Q196 96 216 90" color={C.ok} width={2.4} />
      <Label x={152} y={126} color={C.ok} size={10} bold>
        se umakne
      </Label>
      <Label x={152} y={140} color={C.ok} size={10}>
        zavije desno
      </Label>

      <Glider x={272} y={72} scale={0.85} flip />
      <Label x={272} y={44} color={C.dim} size={10}>
        pobočje na desni
      </Label>
      <Tick x={272} y={106} />
      <Label x={272} y={140} color={C.dim} size={10} bold>
        ima prednost
      </Label>
    </>
  ),

  // 15 — negativni zavoj
  15: (
    <>
      <path d="M108 56 Q160 32 212 56" fill="none" stroke={C.brand} strokeWidth="3.2" strokeLinecap="round" />
      <line x1="116" y1="58" x2="160" y2="124" stroke={C.brand} strokeWidth="1.4" />
      <line x1="204" y1="58" x2="160" y2="124" stroke={C.brand} strokeWidth="1.4" />
      <circle cx="160" cy="128" r="4.5" fill={C.brand} />
      <Arrow d="M116 66 L116 104" color={C.bad} width={3} />
      <Label x={110} y={118} color={C.bad} anchor="end" size={10} bold>
        močno zavrta
      </Label>
      <path d="M212 66 Q244 84 226 110" fill="none" stroke={C.bad} strokeWidth="2.4" strokeLinecap="round" markerEnd="url(#pt)" />
      <Label x={248} y={120} color={C.bad} anchor="start" size={10} bold>
        vrtenje nazaj
      </Label>
      <Label x={160} y={166} color={C.dim} size={10}>
        tok se odtrga na zavrti strani
      </Label>
    </>
  ),

  // 16 — kje zbijaš višino z osmicami
  16: (
    <>
      <Ground />
      <rect x="196" y="140" width="104" height="18" rx="3" fill="var(--surface-3)" stroke={C.line} strokeWidth="1.5" />
      <Label x={248} y={153} color={C.dim} size={10}>
        pristajališče
      </Label>
      <path
        d="M40 40 Q120 54 48 74 Q120 92 52 110 Q110 124 176 142"
        fill="none"
        stroke={C.ok}
        strokeWidth="2.4"
        strokeLinecap="round"
      />
      <Tick x={34} y={32} />
      <Label x={96} y={30} color={C.ok} anchor="start" size={10} bold>
        osmice PRED pristankom
      </Label>
      <path d="M216 96 Q280 106 220 120" fill="none" stroke={C.bad} strokeWidth="2.2" strokeDasharray="5 4" strokeLinecap="round" />
      <Cross x={292} y={96} />
      <Label x={246} y={90} color={C.bad} size={10} bold>
        ne nad njim
      </Label>
    </>
  ),

  // 17 — pristajalni manever, zadnja stranica
  17: (
    <>
      <Ground />
      <rect x="180" y="138" width="120" height="20" rx="3" fill="var(--surface-3)" stroke={C.line} strokeWidth="1.5" />
      <Arrow d="M296 26 L232 26" />
      <Label x={300} y={20} color={C.dim} anchor="end" size={10}>
        veter
      </Label>
      <path d="M40 40 L232 40" fill="none" stroke={C.brand} strokeWidth="2.4" strokeLinecap="round" />
      <Label x={136} y={32} color={C.dim} size={10}>
        1. z vetrom
      </Label>
      <path d="M40 40 L40 96" fill="none" stroke={C.brand} strokeWidth="2.4" strokeLinecap="round" />
      <Label x={34} y={72} color={C.dim} anchor="end" size={10}>
        2. prečno
      </Label>
      <path d="M40 96 L176 144" fill="none" stroke={C.ok} strokeWidth="3" strokeLinecap="round" markerEnd="url(#pt)" />
      <Label x={116} y={98} color={C.ok} anchor="start" size={10} bold>
        3. vsaj 50 m naravnost
      </Label>
      <Tick x={196} y={120} />
    </>
  ),
};

export const hasDiagram = (ord) => Object.prototype.hasOwnProperty.call(D, ord);

export function ExamDiagram({ ord, title }) {
  const body = D[ord];
  if (!body) return null;

  return (
    <figure style={{ margin: 0 }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        role="img"
        aria-label={title ? `Shema: ${title}` : 'Shema'}
        style={{
          background: 'var(--surface-2)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-md)',
          display: 'block',
        }}
      >
        <defs>
          <marker id="pt" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
            <path d="M0 1 L9 5 L0 9 z" fill="context-stroke" />
          </marker>
        </defs>
        {body}
      </svg>
    </figure>
  );
}
